import { FC, HTMLAttributes, JSX } from 'react';
import { IconType } from 'react-icons';
import { FiPlus } from 'react-icons/fi';
import { RiEdit2Line } from 'react-icons/ri';
import { Button } from '../button/Button';
import { IconButton } from '../button/IconButton';
import { Icon, Theme } from '../icons/Icon';
import { Title } from '../text/Title';

export type CardIconButtonIcon = 'edit' | 'add';

const cardIconButtonIconMap: Record<CardIconButtonIcon, IconType> = {
  edit: RiEdit2Line,
  add: FiPlus
};

export interface CardActionButton {
  onClick: () => void;
  label: string;
  anchorId?: string;
}

export interface CardIconButton extends CardActionButton {
  icon?: CardIconButtonIcon;
}

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  descriptor?: string;
  icon?: IconType;
  smallContent?: JSX.Element;
  largeContent?: JSX.Element;
  primaryAction?: CardIconButton;
  secondaryAction?: CardActionButton;
  theme?: Theme;
  longString?: boolean;
}

export const CardHeader: FC<CardHeaderProps> = ({
  title,
  descriptor,
  icon,
  smallContent,
  largeContent,
  primaryAction,
  secondaryAction,
  longString,
  theme = 'primary',
  ...props
}) => {
  const theTitle = <span>{title}</span>;

  return (
    <div className="flex flex-row p-4 pb-0 gap-5 items-center" {...props}>
      <Title className="flex items-center space-x-2">
        {icon && <Icon theme={theme} Icon={icon} />}
        <div className="space-x-2 flex items-center justify-center">
          {longString ? (
            <div className="flex flex-col">
              {theTitle}
              {descriptor && <span className="text-sm font-medium text-secondary-std">{descriptor}</span>}
            </div>
          ) : (
            <>
              {theTitle}
              {descriptor && (
                <>
                  <span className="text-tertiary-std">{'•'}</span>
                  <span className="text-sm font-medium text-secondary-std">{descriptor}</span>
                </>
              )}
            </>
          )}
        </div>
      </Title>
      <div className="flex flex-1 gap-5 justify-end">
        <div className="hidden lg:block">{largeContent}</div>
        {smallContent}
        {primaryAction && (
          <IconButton
            className="rounded-full bg-gray-100 dark:bg-gray-500 p-1 h-8 w-8"
            label={primaryAction.label}
            Icon={cardIconButtonIconMap[primaryAction.icon ?? 'edit']}
            onClick={primaryAction.onClick}
            id={primaryAction.anchorId}
          />
        )}
        {secondaryAction && (
          <Button primary onClick={secondaryAction.onClick}>
            {secondaryAction.label}
          </Button>
        )}
      </div>
    </div>
  );
};
